import React from 'react';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import Card from '../../../Presentation/components/ui/Card';
import StatsCards from '../../../Presentation/components/common/StatsCards';
import {AppText, AppSectionTitle, AppBodyText} from '../../../Presentation/components/ui/Typography';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';
import {formatNumber} from '../../../Presentation/utils/numberUtils';
import {Receipt, DollarSign, CheckCircle, Clock} from 'lucide-react';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';
import type {SalesStatistics as SalesStatisticsType} from '../models/sale.model';

interface SalesStatisticsProps {
  statistics: SalesStatisticsType | null;
  loading?: boolean;
}

const SalesStatistics: React.FC<SalesStatisticsProps> = ({
  statistics,
  loading = false
}) => {
  const {hasPermission} = useAuthorization();

  if (!hasPermission(PERMISSIONS.VIEW_FINANCIAL_REPORTS) && !hasPermission(PERMISSIONS.MANAGE_SALES)) {
    return (
      <PermissionDeniedCard message="ليس لديك صلاحيات لعرض إحصائيات المبيعات. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة." />
    );
  }

  if (!statistics && !loading) {
    return (
      <Card className="p-6">
        <AppText className="text-gray-500 text-center">لا توجد إحصائيات متاحة</AppText>
      </Card>
    );
  }

  const statsCards = statistics
    ? [
        { title: 'إجمالي الفواتير', value: formatNumber(statistics.total_invoices || 0), icon: <Receipt className="w-6 h-6 text-primary" />, bgColor: 'bg-primary/10', textColor: 'text-primary' },
        { title: 'إجمالي المبلغ', value: formatCurrency(statistics.total_amount || 0), icon: <DollarSign className="w-6 h-6 text-success" />, bgColor: 'bg-success/10', textColor: 'text-success' },
        { title: 'المبلغ المدفوع', value: formatCurrency(statistics.total_paid || 0), icon: <CheckCircle className="w-6 h-6 text-success" />, bgColor: 'bg-success/10', textColor: 'text-success' },
        { title: 'المبلغ المتبقي', value: formatCurrency(statistics.total_remaining || 0), icon: <Clock className="w-6 h-6 text-warning" />, bgColor: 'bg-warning/10', textColor: 'text-warning' },
        { title: 'الفواتير المدفوعة', value: formatNumber(statistics.paid_invoices || 0), icon: <CheckCircle className="w-6 h-6 text-success" />, bgColor: 'bg-success/10', textColor: 'text-success' },
        { title: 'الفواتير غير المدفوعة', value: formatNumber(statistics.unpaid_invoices || 0), icon: <Clock className="w-6 h-6 text-error" />, bgColor: 'bg-error/10', textColor: 'text-error' },
        { title: 'الفواتير المدفوعة جزئياً', value: formatNumber(statistics.partially_paid_invoices || 0), icon: <Clock className="w-6 h-6 text-warning" />, bgColor: 'bg-warning/10', textColor: 'text-warning' },
        { title: 'إجمالي المستحقات', value: formatCurrency(statistics.total_outstanding || 0), icon: <DollarSign className="w-6 h-6 text-primary" />, bgColor: 'bg-primary/10', textColor: 'text-primary' }
      ]
    : [];

  return (
    <div className="space-y-6">
      <StatsCards cards={statsCards} loading={loading} columns={4} loadingRows={8} className="mb-0" />

      {statistics && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <AppSectionTitle id="invoice-status-breakdown" className="font-semibold text-gray-900 mb-4">توزيع الفواتير حسب الحالة</AppSectionTitle>
              <div role="list" aria-labelledby="invoice-status-breakdown" className="space-y-3">
                <div role="listitem" className="flex justify-between items-center">
                  <AppBodyText className="text-gray-600">مدفوعة بالكامل</AppBodyText>
                  <AppText className="font-semibold text-success">{formatNumber(statistics.paid_invoices || 0)}</AppText>
                </div>
                <div role="listitem" className="flex justify-between items-center">
                  <AppBodyText className="text-gray-600">مدفوعة جزئياً</AppBodyText>
                  <AppText className="font-semibold text-warning">{formatNumber(statistics.partially_paid_invoices || 0)}</AppText>
                </div>
                <div role="listitem" className="flex justify-between items-center">
                  <AppBodyText className="text-gray-600">غير مدفوعة</AppBodyText>
                  <AppText className="font-semibold text-error">{formatNumber(statistics.unpaid_invoices || 0)}</AppText>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <AppSectionTitle id="aging-report" className="font-semibold text-gray-900 mb-4">تقرير الأعمار</AppSectionTitle>
              <div role="list" aria-labelledby="aging-report" className="space-y-3">
                <div role="listitem" className="flex justify-between items-center">
                  <AppBodyText className="text-gray-600">الحالي</AppBodyText>
                  <AppText className="font-semibold text-primary">{formatCurrency(statistics.aging_report?.current || 0)}</AppText>
                </div>
                <div role="listitem" className="flex justify-between items-center">
                  <AppBodyText className="text-gray-600">1-30 يوم</AppBodyText>
                  <AppText className="font-semibold text-success">{formatCurrency(statistics.aging_report?.days_1_30 || 0)}</AppText>
                </div>
                <div role="listitem" className="flex justify-between items-center">
                  <AppBodyText className="text-gray-600">31-60 يوم</AppBodyText>
                  <AppText className="font-semibold text-warning">{formatCurrency(statistics.aging_report?.days_31_60 || 0)}</AppText>
                </div>
                <div role="listitem" className="flex justify-between items-center">
                  <AppBodyText className="text-gray-600">61-90 يوم</AppBodyText>
                  <AppText className="font-semibold text-warning">{formatCurrency(statistics.aging_report?.days_61_90 || 0)}</AppText>
                </div>
                <div role="listitem" className="flex justify-between items-center">
                  <AppBodyText className="text-gray-600">أكثر من 90 يوم</AppBodyText>
                  <AppText className="font-semibold text-error">{formatCurrency(statistics.aging_report?.over_90 || 0)}</AppText>
                </div>
              </div>
            </Card>
          </div>

          {statistics.top_customers && statistics.top_customers.length > 0 && (
            <Card className="p-6">
              <AppSectionTitle id="top-customers" className="font-semibold text-gray-900 mb-4">أفضل العملاء</AppSectionTitle>
              <div role="list" aria-labelledby="top-customers" className="space-y-3">
                {statistics.top_customers.slice(0, 5).map((customer, index) => (
                  <div key={`${customer.customer_name}-${index}`} role="listitem" className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-primary">{index + 1}</span>
                      </div>
                      <div>
                        <AppText className="font-medium text-gray-900">{customer.customer_name}</AppText>
                        <AppBodyText className="text-gray-600">مدفوع: {formatCurrency(customer.paid_amount)}</AppBodyText>
                      </div>
                    </div>
                    <AppText className="font-bold text-success">{formatCurrency(customer.total_amount)}</AppText>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default SalesStatistics;
